/**
 * Context xác thực người dùng — quản lý session và các phương thức đăng nhập/đăng ký.
 *
 * Luồng dữ liệu:
 * - Mount: đọc token từ localStorage → gọi getCurrentUser để khôi phục session.
 * - Login/register/OAuth: persistSession lưu token + set user state.
 * - logout: gọi API, xóa token, reset user; hiển thị overlay loading tối thiểu.
 * - Consumer dùng useAuth() để đọc user, isAuthenticated, isAdmin, các hàm auth.
 */
import React, { createContext, useState, useEffect } from 'react';
import { InlineLoadingIndicator } from '../components/Common/InlineLoadingIndicator';
import { authApi } from '../features/auth/api/authApi';
import { ADMIN_ROLES } from '../shared/constants/users';
import { TEXT } from '../shared/languages';
import { waitMinimumDelay } from '../shared/utils/waitMinimumDelay';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  /** Thông tin user đã đăng nhập; null khi chưa auth */
  const [user, setUser] = useState(null);
  /** true trong lúc kiểm tra token ban đầu */
  const [loading, setLoading] = useState(true);
  /** true khi đang thực hiện logout — hiển thị overlay */
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  /** Khôi phục session từ token lưu trong localStorage */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi
        .getCurrentUser()
        .then((response) => {
          setUser(response.data.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  /** Lưu token và cập nhật user sau đăng nhập/đăng ký thành công */
  const persistSession = (response) => {
    const { token, user: authenticatedUser } = response.data.data;
    localStorage.setItem('token', token);
    setUser(authenticatedUser);
    return authenticatedUser;
  };

  const login = (email, password) => {
    return authApi
      .login({ email, password })
      .then(persistSession);
  };

  const loginWithGoogle = (idToken) => {
    return authApi
      .loginWithGoogle({ id_token: idToken })
      .then(persistSession);
  };

  const loginWithApple = (idToken, name = null) => {
    return authApi
      .loginWithApple({ id_token: idToken, name })
      .then(persistSession);
  };

  /** Đăng xuất: gọi API, đảm bảo delay tối thiểu cho UX, xóa token local */
  const logout = async () => {
    setIsLoggingOut(true);
    const startedAt = Date.now();

    try {
      await authApi.logout();
    } finally {
      await waitMinimumDelay(startedAt);
      localStorage.removeItem('token');
      setUser(null);
      setIsLoggingOut(false);
    }
  };

  const register = (name, email, password, phone = '') =>
    authApi.register({ name, email, password, phone }).then(persistSession);

  const value = {
    user,
    loading,
    isLoggingOut,
    isAuthenticated: !!user,
    isAdmin: ADMIN_ROLES.includes(user?.role),
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    register,
    /** Làm mới thông tin user từ server (sau cập nhật profile) */
    refreshUser: () =>
      authApi.getCurrentUser().then((response) => {
        setUser(response.data.data);
        return response.data.data;
      }),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isLoggingOut && (
        <InlineLoadingIndicator overlay label={TEXT.auth.logoutSubmitting} />
      )}
    </AuthContext.Provider>
  );
};
